/**
 * Advertiser publisher-stats page (feature #148).
 *
 * Renders:
 *   - a compact query form (advertiser ID + date range)
 *   - summary KPI strip (clicks / conversions / revenue / publishers)
 *   - sortable per-publisher table (name, clicks, conversions, CR%, EPC,
 *     revenue, payout) with click-to-sort headers
 *   - top-10 bar list ranked by conversions (separate /top query)
 *
 * State is local; TanStack Query owns remote lifecycle. The form layer is a
 * hand-rolled reducer-free shape mirroring `smartlinks-analytics` to keep
 * the codebase pattern consistent.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	advertiserPublisherStatsQueryKeys,
	barWidth,
	fetchAdvertiserPublisherRange,
	fetchAdvertiserPublisherTop,
	formatCents,
	formatCrPercent,
	formatEpc,
	formatInteger,
	rangeQuerySchema,
	sortPublishers,
	type PublisherAggregate,
	type RangeResponse,
	type SortDir,
	type SortKey,
	type TopResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Brand tokens (keep inline so the root wire-up agent can grep them).
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

// ---------------------------------------------------------------------------
// Default date range — last 7 days ending today (UTC).
// ---------------------------------------------------------------------------

export function defaultDateRange(now: Date = new Date()): {
	from: string;
	to: string;
} {
	const end = new Date(
		Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
	);
	const start = new Date(end);
	start.setUTCDate(start.getUTCDate() - 6);
	return { from: toIso(start), to: toIso(end) };
}

function toIso(d: Date): string {
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
	advertiserId: string;
	from: string;
	to: string;
}

export interface AdvertiserPublisherStatsPageProps {
	/** Prefills (but does not lock) the advertiser_id field. */
	initialAdvertiserId?: string;
}

export function AdvertiserPublisherStatsPage({
	initialAdvertiserId,
}: AdvertiserPublisherStatsPageProps = {}) {
	const initialRange = useMemo(() => defaultDateRange(), []);
	const [form, setForm] = useState<FormState>({
		advertiserId: initialAdvertiserId ?? "",
		from: initialRange.from,
		to: initialRange.to,
	});
	const [submitted, setSubmitted] = useState<FormState | null>(() =>
		initialAdvertiserId
			? {
					advertiserId: initialAdvertiserId,
					from: initialRange.from,
					to: initialRange.to,
				}
			: null,
	);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [sortKey, setSortKey] = useState<SortKey>("conversions");
	const [sortDir, setSortDir] = useState<SortDir>("desc");

	const parsedSubmission = useMemo(() => {
		if (!submitted) return null;
		return rangeQuerySchema.safeParse(submitted);
	}, [submitted]);

	const queryEnabled = Boolean(parsedSubmission?.success);

	const rangeQuery = useQuery<RangeResponse | null>({
		queryKey: parsedSubmission?.success
			? advertiserPublisherStatsQueryKeys.range(
					parsedSubmission.data.advertiserId,
					parsedSubmission.data.from,
					parsedSubmission.data.to,
				)
			: advertiserPublisherStatsQueryKeys.all,
		queryFn: () => {
			if (!parsedSubmission?.success) {
				throw new Error("Invalid query — submit the form first.");
			}
			return fetchAdvertiserPublisherRange({
				advertiserId: parsedSubmission.data.advertiserId,
				from: parsedSubmission.data.from,
				to: parsedSubmission.data.to,
			});
		},
		enabled: queryEnabled,
	});

	const topQuery = useQuery<TopResponse | null>({
		queryKey: parsedSubmission?.success
			? advertiserPublisherStatsQueryKeys.top(
					parsedSubmission.data.advertiserId,
					10,
				)
			: advertiserPublisherStatsQueryKeys.all,
		queryFn: () => {
			if (!parsedSubmission?.success) {
				throw new Error("Invalid query — submit the form first.");
			}
			return fetchAdvertiserPublisherTop({
				advertiserId: parsedSubmission.data.advertiserId,
				limit: 10,
			});
		},
		enabled: queryEnabled,
	});

	const summary = rangeQuery.data ?? null;
	const topSummary = topQuery.data ?? null;

	const sortedPublishers = useMemo(() => {
		if (!summary) return [];
		return sortPublishers(summary.publishers, sortKey, sortDir);
	}, [summary, sortKey, sortDir]);

	function updateField<K extends keyof FormState>(key: K, value: string) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = rangeQuerySchema.safeParse(form);
		if (!parsed.success) {
			setErrors(parsed.errors);
			return;
		}
		setErrors({});
		setSubmitted({ ...form });
	}

	function handleSortHeaderClick(key: SortKey) {
		if (sortKey === key) {
			setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(key);
			setSortDir(key === "affiliate_name" ? "asc" : "desc");
		}
	}

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<QueryForm
				value={form}
				errors={errors}
				loading={rangeQuery.isFetching || topQuery.isFetching}
				onChange={updateField}
				onSubmit={handleSubmit}
			/>
			<SummarySection
				summary={summary}
				sortedPublishers={sortedPublishers}
				isLoading={queryEnabled && rangeQuery.isLoading}
				isError={rangeQuery.isError}
				errorMessage={
					rangeQuery.error instanceof Error
						? rangeQuery.error.message
						: undefined
				}
				hasSubmission={Boolean(submitted)}
				sortKey={sortKey}
				sortDir={sortDir}
				onSortHeaderClick={handleSortHeaderClick}
			/>
			<TopSection
				top={topSummary}
				hasSubmission={Boolean(submitted)}
				isLoading={queryEnabled && topQuery.isLoading}
			/>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Breadcrumb + heading
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<a
						href="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</a>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Advertisers</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Publisher stats</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Advertisers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Publisher stats
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Inspect per-partner click and conversion performance for an
				advertiser. Numbers come from the nightly (advertiser, publisher,
				day) rollup and refresh as soon as the worker flushes.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Query form
// ---------------------------------------------------------------------------

function QueryForm(props: {
	value: FormState;
	errors: Record<string, string>;
	loading: boolean;
	onChange: <K extends keyof FormState>(key: K, value: string) => void;
	onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
	return (
		<form
			onSubmit={props.onSubmit}
			className={`${CARD} flex flex-col gap-5 p-6`}
			noValidate
			aria-labelledby="advertiser-publisher-stats-form-title"
		>
			<div className="flex flex-col gap-1">
				<h2
					id="advertiser-publisher-stats-form-title"
					className="text-base font-semibold text-zinc-900"
				>
					Query
				</h2>
				<p className={HINT}>
					Enter an advertiser and a reporting window.
				</p>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.5fr_1fr_1fr]">
				<Field
					id="advertiser-id"
					label="Advertiser ID"
					error={props.errors.advertiserId}
					hint="Identifier of the advertiser to report on."
				>
					<input
						id="advertiser-id"
						name="advertiserId"
						type="text"
						autoComplete="off"
						placeholder="e.g. adv_acme"
						className={INPUT}
						value={props.value.advertiserId}
						onChange={(e) =>
							props.onChange("advertiserId", e.currentTarget.value)
						}
						aria-invalid={Boolean(props.errors.advertiserId)}
					/>
				</Field>

				<Field
					id="publisher-stats-from"
					label="From"
					error={props.errors.from}
					hint="Start date (UTC), inclusive."
				>
					<input
						id="publisher-stats-from"
						name="from"
						type="date"
						className={INPUT}
						value={props.value.from}
						onChange={(e) => props.onChange("from", e.currentTarget.value)}
						aria-invalid={Boolean(props.errors.from)}
					/>
				</Field>

				<Field
					id="publisher-stats-to"
					label="To"
					error={props.errors.to}
					hint="End date (UTC), inclusive."
				>
					<input
						id="publisher-stats-to"
						name="to"
						type="date"
						className={INPUT}
						value={props.value.to}
						onChange={(e) => props.onChange("to", e.currentTarget.value)}
						aria-invalid={Boolean(props.errors.to)}
					/>
				</Field>
			</div>

			<div className="flex items-center justify-between border-t border-zinc-200 pt-4">
				<p className={HINT} aria-live="polite">
					{props.loading ? "Loading stats." : "Ready."}
				</p>
				<button
					type="submit"
					disabled={props.loading}
					className="rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Run report
				</button>
			</div>
		</form>
	);
}

function Field(props: {
	id: string;
	label: string;
	error?: string;
	hint?: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			{props.children}
			{props.error ? (
				<p className={ERROR_TEXT} role="alert">
					{props.error}
				</p>
			) : props.hint ? (
				<p className={HINT}>{props.hint}</p>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Summary + sortable publisher table
// ---------------------------------------------------------------------------

function SummarySection(props: {
	summary: RangeResponse | null;
	sortedPublishers: PublisherAggregate[];
	isLoading: boolean;
	isError: boolean;
	errorMessage?: string;
	hasSubmission: boolean;
	sortKey: SortKey;
	sortDir: SortDir;
	onSortHeaderClick: (key: SortKey) => void;
}) {
	if (!props.hasSubmission) {
		return (
			<section aria-label="Summary" className={`${CARD} p-6`}>
				<p className={HINT}>
					Run a report to see per-publisher stats for an advertiser.
				</p>
			</section>
		);
	}

	if (props.isLoading) {
		return (
			<section aria-label="Summary" className={`${CARD} p-6`}>
				<p className={HINT} aria-busy="true">
					Loading publisher stats.
				</p>
			</section>
		);
	}

	if (props.isError) {
		return (
			<section
				aria-label="Summary"
				className={`${CARD} border-red-200 bg-red-50 p-6`}
			>
				<p className="text-sm text-red-700" role="alert">
					{props.errorMessage ?? "Unable to load stats."}
				</p>
			</section>
		);
	}

	if (!props.summary || props.summary.publishers.length === 0) {
		return (
			<section aria-label="Summary" className={`${CARD} p-6`}>
				<p className={HINT}>
					No publisher activity for this advertiser in the selected window.
				</p>
			</section>
		);
	}

	const { summary } = props;

	return (
		<div className="flex flex-col gap-6">
			<KpiStrip
				clicks={summary.totals.clicks}
				conversions={summary.totals.conversions}
				revenueCents={summary.totals.revenue_cents}
				publisherCount={summary.publishers.length}
			/>
			<PublisherTable
				publishers={props.sortedPublishers}
				sortKey={props.sortKey}
				sortDir={props.sortDir}
				onSortHeaderClick={props.onSortHeaderClick}
			/>
		</div>
	);
}

function KpiStrip(props: {
	clicks: number;
	conversions: number;
	revenueCents: number;
	publisherCount: number;
}) {
	return (
		<section
			aria-label="Summary KPIs"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
		>
			<Kpi label="Clicks" value={formatInteger(props.clicks)} />
			<Kpi label="Conversions" value={formatInteger(props.conversions)} />
			<Kpi label="Revenue" value={formatCents(props.revenueCents)} />
			<Kpi label="Publishers" value={formatInteger(props.publisherCount)} />
		</section>
	);
}

function Kpi(props: { label: string; value: string }) {
	return (
		<div className={`${CARD} flex flex-col gap-1 p-4`}>
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				{props.label}
			</p>
			<p className="text-2xl font-semibold text-zinc-900">{props.value}</p>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sortable publisher table (name / clicks / conv / CR / EPC / revenue)
// ---------------------------------------------------------------------------

export function PublisherTable(props: {
	publishers: PublisherAggregate[];
	sortKey: SortKey;
	sortDir: SortDir;
	onSortHeaderClick: (key: SortKey) => void;
}) {
	return (
		<section
			aria-label="Publisher breakdown"
			className={`${CARD} overflow-hidden`}
		>
			<table className="w-full border-collapse text-sm">
				<thead className="bg-zinc-50 text-left">
					<tr>
						<SortableTh
							label="Publisher"
							sortKey="affiliate_name"
							activeKey={props.sortKey}
							dir={props.sortDir}
							onClick={props.onSortHeaderClick}
						/>
						<SortableTh
							label="Clicks"
							sortKey="clicks"
							activeKey={props.sortKey}
							dir={props.sortDir}
							onClick={props.onSortHeaderClick}
							className="text-right"
						/>
						<SortableTh
							label="Conv"
							sortKey="conversions"
							activeKey={props.sortKey}
							dir={props.sortDir}
							onClick={props.onSortHeaderClick}
							className="text-right"
						/>
						<th
							scope="col"
							className="border-b border-zinc-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500 text-right"
						>
							CR
						</th>
						<SortableTh
							label="EPC"
							sortKey="epc_cents"
							activeKey={props.sortKey}
							dir={props.sortDir}
							onClick={props.onSortHeaderClick}
							className="text-right"
						/>
						<SortableTh
							label="Revenue"
							sortKey="revenue_cents"
							activeKey={props.sortKey}
							dir={props.sortDir}
							onClick={props.onSortHeaderClick}
							className="text-right"
						/>
					</tr>
				</thead>
				<tbody>
					{props.publishers.map((pub) => (
						<PublisherRow key={pub.affiliate_id} publisher={pub} />
					))}
				</tbody>
			</table>
		</section>
	);
}

function SortableTh(props: {
	label: string;
	sortKey: SortKey;
	activeKey: SortKey;
	dir: SortDir;
	onClick: (key: SortKey) => void;
	className?: string;
}) {
	const isActive = props.sortKey === props.activeKey;
	const arrow = isActive ? (props.dir === "asc" ? "▲" : "▼") : "";
	return (
		<th
			scope="col"
			aria-sort={
				isActive
					? props.dir === "asc"
						? "ascending"
						: "descending"
					: "none"
			}
			className={`border-b border-zinc-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500 ${
				props.className ?? ""
			}`}
		>
			<button
				type="button"
				onClick={() => props.onClick(props.sortKey)}
				className="inline-flex items-center gap-1 hover:text-zinc-900"
				data-testid={`sort-${props.sortKey}`}
			>
				<span>{props.label}</span>
				<span aria-hidden="true" className="text-[10px]">
					{arrow}
				</span>
			</button>
		</th>
	);
}

function PublisherRow(props: { publisher: PublisherAggregate }) {
	const { publisher } = props;
	const displayName = publisher.affiliate_name ?? publisher.affiliate_id;
	return (
		<tr
			data-testid="publisher-row"
			data-affiliate-id={publisher.affiliate_id}
			className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
		>
			<td className="px-4 py-3 text-zinc-800">
				<div className="flex flex-col">
					<span className="font-medium">{displayName}</span>
					<span className="font-mono text-xs text-zinc-500">
						{publisher.affiliate_id}
					</span>
				</div>
			</td>
			<td className="px-4 py-3 text-right tabular-nums text-zinc-800">
				{formatInteger(publisher.clicks)}
			</td>
			<td className="px-4 py-3 text-right tabular-nums text-zinc-800">
				{formatInteger(publisher.conversions)}
			</td>
			<td
				className="px-4 py-3 text-right tabular-nums text-zinc-800"
				data-testid="publisher-cr"
			>
				{formatCrPercent(publisher.clicks, publisher.conversions)}
			</td>
			<td
				className="px-4 py-3 text-right tabular-nums text-zinc-800"
				data-testid="publisher-epc"
			>
				{formatEpc(publisher.epc_cents)}
			</td>
			<td className="px-4 py-3 text-right tabular-nums text-zinc-800">
				{formatCents(publisher.revenue_cents)}
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Top-10 bar list
// ---------------------------------------------------------------------------

function TopSection(props: {
	top: TopResponse | null;
	hasSubmission: boolean;
	isLoading: boolean;
}) {
	if (!props.hasSubmission) return null;
	if (props.isLoading) {
		return (
			<section aria-label="Top publishers" className={`${CARD} p-6`}>
				<p className={HINT} aria-busy="true">
					Loading top publishers.
				</p>
			</section>
		);
	}
	if (!props.top || props.top.publishers.length === 0) {
		return (
			<section aria-label="Top publishers" className={`${CARD} p-6`}>
				<p className={HINT}>No top publishers to show yet.</p>
			</section>
		);
	}

	return <TopPublishersList publishers={props.top.publishers} />;
}

export function TopPublishersList(props: {
	publishers: PublisherAggregate[];
}) {
	const max = props.publishers.reduce(
		(m, p) => Math.max(m, p.conversions),
		0,
	);

	return (
		<section
			aria-label="Top publishers by conversions"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<div className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">
					Top publishers
				</h2>
				<p className={HINT}>
					Ranked by conversions for the selected advertiser, across all
					time.
				</p>
			</div>
			<ol className="flex flex-col gap-3">
				{props.publishers.map((pub, idx) => (
					<li
						key={pub.affiliate_id}
						data-testid="top-publisher-row"
						className="flex flex-col gap-1"
					>
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-2 text-sm text-zinc-800">
								<span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 text-xs font-semibold text-zinc-600">
									{idx + 1}
								</span>
								<span className="font-medium">
									{pub.affiliate_name ?? pub.affiliate_id}
								</span>
								<span className="font-mono text-xs text-zinc-500">
									{pub.affiliate_id}
								</span>
							</div>
							<span className="tabular-nums text-sm text-zinc-700">
								{formatInteger(pub.conversions)} conv
							</span>
						</div>
						<div
							aria-hidden="true"
							className="relative h-2 overflow-hidden rounded-[6px] bg-zinc-100"
						>
							<div
								className="h-full rounded-[6px] bg-gradient-to-r from-green-700 to-green-500"
								style={{ width: barWidth(pub.conversions, max) }}
								data-testid="top-publisher-bar"
							/>
						</div>
					</li>
				))}
			</ol>
		</section>
	);
}

export default AdvertiserPublisherStatsPage;
